import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ShieldCheck, Activity, HeartPulse, LineChart, GraduationCap, Users, Check, Crown,
  ArrowRight, ArrowLeft, ClipboardCheck, Trophy, Palette, Building2,
} from "lucide-react";
import { ACADEMY_LOGO_H } from "../data/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ────────────────────────────────────────────────────────────────
   Official pricing from the Next O High School Performance System PDF.
   ──────────────────────────────────────────────────────────────── */
const TEAM_TIERS = [
  {
    id: "camp", name: "Pre-Season Camp", price: "$1,500–$2,200", cadence: "/ program", tagline: "4-Week High Performance Camp before tryouts.",
    features: [
      "8 sessions (2×/wk · 75 min): ball mastery, RSA conditioning, multi-directional speed & injury prevention",
      "Full team testing — sprint times, vertical, agility metrics",
      "1-month Next O App access during camp",
      "Covers Varsity + JV, up to 40 players",
    ],
  },
  {
    id: "retainer", name: "In-Season Retainer", price: "$600–$900", cadence: "/ month", highlight: true, badge: "Most Popular",
    tagline: "Maintenance & injury-prevention through the season.",
    features: [
      "2×/week 20-min post-practice / recovery modules",
      "Weekly DOC / Coach analytics on player strain & readiness",
      "Keeps your starting XI healthy for the postseason",
      "ACL / hamstring prevention protocols built in",
    ],
  },
  {
    id: "hybrid", name: "Player-Paid Team License", price: "$45–$60", cadence: "/ player", gold: true, badge: "Zero Cost to School",
    tagline: "Parent-funded — free for the program.",
    features: [
      "6-week off-season program, billed direct to parents",
      "FREE administrative dashboard for the head coach",
      "20+ players → $250 equipment credit OR 15% booster revenue share",
      "Next O handles all parent registration",
    ],
  },
];

const INDIVIDUAL_TIERS = [
  {
    id: "academy", name: "High School Academy", price: "$39", cadence: "/ month", tagline: "At-home development for every athlete.",
    features: ["At-Home Technical & RSA Conditioning Library", "Daily training logging", "Team leaderboard rank"],
  },
  {
    id: "elite", name: "High School Elite", price: "$69", cadence: "/ month", highlight: true, badge: "Most Popular",
    tagline: "Periodized strength + coach feedback.",
    features: ["Everything in Academy", "Monthly strength periodization (In-Season vs Off-Season)", "2-sentence performance reviews", "Monthly motion video feedback"],
  },
  {
    id: "collegiate", name: "Collegiate Bound", price: "$149", cadence: "/ month", gold: true, badge: "Premier",
    tagline: "Recruiting-grade analysis & coaching.",
    features: ["Unlimited video telestrator motion analysis", "Weekly voice-note feedback", "Custom weight-training adaptation", "HS match clip review for college recruiting"],
  },
];

const VALUES = [
  { icon: HeartPulse, title: "Injury Prevention", desc: "Daily body-map check-ins flag strain before it becomes a season-ending injury." },
  { icon: Activity, title: "Readiness Index", desc: "A live team readiness score every morning — know who's fresh and who needs managing." },
  { icon: LineChart, title: "Coach Analytics", desc: "Strain heatmaps and red-flag alerts put your whole roster's status on one screen." },
  { icon: Trophy, title: "Athlete Buy-In", desc: "Gamified hubs, streaks and leaderboards keep players training all season long." },
];

const STEPS = [
  { icon: Building2, title: "We set up your school", desc: "Custom colors, mascot and roster — your program's identity across the whole app." },
  { icon: GraduationCap, title: "Athletes get 14-day access", desc: "Share one code. Players redeem an Elite Readiness trial in seconds — no app store needed." },
  { icon: ClipboardCheck, title: "You coach with data", desc: "Track readiness, prevent injuries, and clear athletes to return — all from your dashboard." },
];

const Field = ({ label, ...p }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-widest text-white/45 block mb-1.5">{label}</label>
    <input {...p} className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" />
  </div>
);

const TierCard = ({ t, i, prefix }) => {
  const gold = t.gold;
  const accent = gold ? "#D4AF37" : "#C41E24";
  return (
    <motion.div data-testid={`fs-tier-${t.id}`}
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
      className="relative flex flex-col rounded-2xl p-7"
      style={(t.highlight || gold)
        ? { border: `2px solid ${accent}${gold ? "99" : ""}`, background: `linear-gradient(to bottom, ${accent}1f, #141414)`, boxShadow: `0 24px 70px ${accent}22` }
        : { border: "1px solid rgba(255,255,255,0.1)", background: "#141414" }}>
      {t.badge && (
        <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full whitespace-nowrap"
          style={{ background: accent, color: gold ? "#000" : "#fff" }}><Crown size={13} /> {t.badge}</span>
      )}
      <h3 className="font-headings text-2xl uppercase tracking-wide">{t.name}</h3>
      <p className="text-sm text-white/50 mt-1 mb-5 min-h-[40px]">{t.tagline}</p>
      <div className="flex items-end gap-1 mb-6">
        <span className="font-cover text-3xl sm:text-4xl" style={{ color: gold ? "#D4AF37" : "#fff" }}>{t.price}</span>
        <span className="text-sm text-white/40 mb-1">{t.cadence}</span>
      </div>
      <ul className="space-y-2.5 mb-7 flex-1">
        {t.features.map((f, j) => (
          <li key={j} className="flex items-start gap-2 text-sm text-white/70">
            <Check size={16} className="mt-0.5 shrink-0" style={{ color: accent }} /> {f}
          </li>
        ))}
      </ul>
      <a href="#request" data-testid={`fs-tier-cta-${t.id}`}
        className="w-full flex items-center justify-center gap-2 font-subheadings font-bold uppercase tracking-wider py-3.5 rounded-sm transition-colors"
        style={gold ? { background: "#D4AF37", color: "#000" } : (t.highlight ? { background: "#C41E24", color: "#fff" } : { background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)" })}>
        {prefix} <ArrowRight size={15} />
      </a>
    </motion.div>
  );
};

export default function ForSchools() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", school: "", role: "", team_size: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/school-inquiry`, form);
      setSent(true);
      toast.success("Request received — we'll be in touch shortly.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send. Try again."); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10 safe-top">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="fs-home-link" className="flex items-center gap-2.5">
            <img src={ACADEMY_LOGO_H} alt="The Next O Technical Academy" className="h-12 w-auto object-contain" />
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} className="hidden sm:inline-flex items-center gap-1.5 text-white/60 hover:text-white text-xs font-bold uppercase tracking-wider px-3 py-2 transition-colors"><ArrowLeft size={14} /> Main Site</button>
            <a href="#request" data-testid="fs-header-cta" className="inline-flex items-center gap-1.5 bg-[#D4AF37] hover:bg-[#e8c65a] text-black font-subheadings text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors">Request Demo <ArrowRight size={14} /></a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-20 sm:py-28 px-6 sm:px-10" style={{ background: "linear-gradient(135deg,#C41E2422,transparent 55%)" }}>
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full mb-6">
              <GraduationCap size={14} /> High School Performance System
            </div>
            <h1 className="font-headings text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
              Give your entire program an<br /><span className="text-[#C41E24]">unfair advantage.</span>
            </h1>
            <p className="text-base sm:text-lg text-white/60 mt-6 max-w-2xl">
              The Elite Technical Training System, built for high school &amp; college teams — daily injury-prevention check-ins, a live team readiness index, and a coach dashboard that keeps your whole roster healthy and improving all season.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#request" data-testid="fs-hero-cta" className="inline-flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider px-6 py-3.5 rounded-sm transition-colors" style={{ boxShadow: "0 0 24px #C41E2455" }}>Request Program Pricing <ArrowRight size={16} /></a>
              <a href="#pricing" className="inline-flex items-center gap-2 border border-white/20 hover:border-white/50 text-white font-subheadings font-bold uppercase tracking-wider px-6 py-3.5 rounded-sm transition-colors">View Tiers</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value grid */}
      <section className="py-16 px-6 sm:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {VALUES.map((v, i) => (
            <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
              className="rounded-xl border border-white/10 bg-[#111] p-6">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: "#C41E2422", border: "1px solid #C41E2455" }}><v.icon size={22} className="text-[#C41E24]" /></div>
              <h3 className="font-subheadings text-base font-bold uppercase tracking-wide mb-1.5">{v.title}</h3>
              <p className="text-sm text-white/50">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Dashboard preview */}
      <section className="py-16 px-6 sm:px-10 bg-[#0d0d0d] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D4AF37] mb-3">The Coach's Command Center</div>
            <h2 className="font-headings text-3xl sm:text-4xl uppercase tracking-tight">See your whole squad's readiness at a glance</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <figure className="rounded-2xl border border-white/10 overflow-hidden bg-black">
              <img loading="lazy" decoding="async" src="/marketing/coach-readiness.png" alt="Coach readiness dashboard" className="w-full h-auto" data-testid="fs-preview-coach" />
              <figcaption className="px-5 py-3 text-xs text-white/50 border-t border-white/10">Team Readiness Index, strain heatmap &amp; red-flag alerts.</figcaption>
            </figure>
            <figure className="rounded-2xl border border-white/10 overflow-hidden bg-black">
              <img loading="lazy" decoding="async" src="/marketing/hub-readiness.png" alt="Player readiness check-in" className="w-full h-auto" data-testid="fs-preview-hub" />
              <figcaption className="px-5 py-3 text-xs text-white/50 border-t border-white/10">The athlete's 60-second daily check-in — branded to your school.</figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 px-6 sm:px-10">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative rounded-xl border border-white/10 bg-[#111] p-6">
              <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#C41E24] flex items-center justify-center font-cover text-sm">{i + 1}</div>
              <s.icon size={24} className="text-[#D4AF37] mb-3" />
              <h3 className="font-subheadings text-base font-bold uppercase tracking-wide mb-1.5">{s.title}</h3>
              <p className="text-sm text-white/50">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 px-6 sm:px-10 bg-[#0d0d0d] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Team & Program Packages</div>
            <h2 className="font-headings text-3xl sm:text-4xl uppercase tracking-tight">Built for your whole program</h2>
            <p className="text-sm text-white/50 mt-3">Multi-team &amp; district pricing available on request. The Player-Paid license is <span className="text-white/70">zero cost</span> to the school.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-start">
            {TEAM_TIERS.map((t, i) => <TierCard key={t.id} t={t} i={i} prefix="Request This Plan" />)}
          </div>

          {/* Individual player plans */}
          <div className="text-center max-w-2xl mx-auto mt-20 mb-10">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D4AF37] mb-3">For Individual Athletes</div>
            <h2 className="font-headings text-3xl sm:text-4xl uppercase tracking-tight">Player subscriptions</h2>
            <p className="text-sm text-white/50 mt-3">Parent-funded monthly plans your athletes can start on their own.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-start">
            {INDIVIDUAL_TIERS.map((t, i) => <TierCard key={t.id} t={t} i={i} prefix="Get Started" />)}
          </div>
        </div>
      </section>

      {/* Request / inquiry */}
      <section id="request" className="py-20 px-6 sm:px-10">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Get Started</div>
            <h2 className="font-headings text-3xl sm:text-4xl uppercase tracking-tight">Bring it to your program</h2>
            <p className="text-sm text-white/50 mt-3">Tell us about your team and we'll send program pricing + a live demo login.</p>
          </div>

          {sent ? (
            <div data-testid="fs-inquiry-success" className="rounded-2xl border border-[#22C55E]/40 bg-[#22C55E]/10 p-8 text-center">
              <ShieldCheck size={40} className="text-[#22C55E] mx-auto mb-4" />
              <h3 className="font-headings text-2xl uppercase tracking-wide mb-2">Request received</h3>
              <p className="text-white/60 text-sm">Thanks {form.name.split(" ")[0]} — we'll reach out to {form.email} shortly with pricing and a demo.</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-[#111] p-6 sm:p-8 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name*" value={form.name} onChange={set("name")} data-testid="fs-name" placeholder="Coach / AD name" />
                <Field label="Email*" type="email" value={form.email} onChange={set("email")} data-testid="fs-email" placeholder="you@school.edu" />
                <Field label="School / Program" value={form.school} onChange={set("school")} data-testid="fs-school" placeholder="Lincoln High School" />
                <Field label="Your role" value={form.role} onChange={set("role")} data-testid="fs-role" placeholder="Head Coach, Athletic Director…" />
              </div>
              <Field label="Roster size" value={form.team_size} onChange={set("team_size")} data-testid="fs-team-size" placeholder="e.g. 24 athletes, 2 teams" />
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/45 block mb-1.5">Anything else?</label>
                <textarea data-testid="fs-message" rows={4} value={form.message} onChange={set("message")} placeholder="Season timing, number of teams, questions…"
                  className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" />
              </div>
              <button data-testid="fs-submit" onClick={submit} disabled={sending}
                className="w-full flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider py-3.5 rounded-sm transition-colors disabled:opacity-60">
                {sending ? "Sending…" : "Request Program Pricing"} <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 px-6 text-center text-xs text-white/40">
        <button onClick={() => navigate("/")} className="hover:text-white transition-colors">← Back to Next O Soccer</button>
        <div className="mt-2">© {new Date().getFullYear()} Next O Soccer · High School Performance System</div>
      </footer>
    </div>
  );
}
